import axiosInstance from '../axiosConfig';
import {
  GuestPass,
  GuestPassFormData,
  GuestPassResponse,
  GuestPassesResponse,
} from '../../types';

/**
 * Guest Pass Service
 * Handles all guest pass related API calls
 */
class GuestPassService {
  private baseUrl = '/guest-passes';

  /**
   * Get all guest passes with optional filters
   */
  async getAll(params?: {
    start_date?: string;
    end_date?: string;
    status?: string;
    sponsor_id?: string;
    guest_id?: string;
  }): Promise<GuestPassesResponse> {
    const response = await axiosInstance.get(this.baseUrl, { params });
    return response.data;
  }

  /**
   * Get guest pass by ID
   */
  async getById(id: string): Promise<GuestPassResponse> {
    const response = await axiosInstance.get(`${this.baseUrl}/${id}`);
    return response.data;
  }

  /**
   * Get today's guest passes
   */
  async getTodaysPasses(): Promise<GuestPassesResponse> {
    const response = await axiosInstance.get(`${this.baseUrl}/today`);
    return response.data;
  }

  /**
   * Get passes pending check-in
   */
  async getPendingCheckin(): Promise<GuestPassesResponse> {
    const response = await axiosInstance.get(`${this.baseUrl}/pending-checkin`);
    return response.data;
  }

  /**
   * Get currently active passes (checked in, not yet checked out)
   */
  async getActivePasses(): Promise<GuestPassesResponse> {
    const response = await axiosInstance.get(`${this.baseUrl}/active`);
    return response.data;
  }

  /**
   * Get upcoming passes (future dates)
   */
  async getUpcomingPasses(): Promise<GuestPassesResponse> {
    const response = await axiosInstance.get(`${this.baseUrl}/upcoming`);
    return response.data;
  }

  /**
   * Get guest passes for a specific guest
   */
  async getByGuest(guestId: string): Promise<GuestPassesResponse> {
    const response = await axiosInstance.get(`${this.baseUrl}/guest/${guestId}`);
    return response.data;
  }

  /**
   * Get guest passes issued by a specific sponsor
   */
  async getBySponsor(sponsorId: string): Promise<GuestPassesResponse> {
    const response = await axiosInstance.get(`${this.baseUrl}/sponsor/${sponsorId}`);
    return response.data;
  }

  /**
   * Issue a new guest pass
   */
  async create(data: GuestPassFormData): Promise<GuestPassResponse> {
    const response = await axiosInstance.post(this.baseUrl, data);
    return response.data;
  }

  /**
   * Issue a guest pass to a new guest (creates non-member account)
   */
  async createWithNewGuest(data: {
    sponsor_member_id: string;
    visit_date: string;
    guest_first_name: string;
    guest_last_name: string;
    guest_email: string;
    guest_phone?: string;
    facilities_allowed?: string[];
    notes?: string;
  }): Promise<GuestPassResponse> {
    const response = await axiosInstance.post(`${this.baseUrl}/with-new-guest`, data);
    return response.data;
  }

  /**
   * Check in a guest
   */
  async checkIn(
    passId: string,
    notes?: string
  ): Promise<GuestPassResponse> {
    const response = await axiosInstance.post(`${this.baseUrl}/${passId}/check-in`, { notes });
    return response.data;
  }

  /**
   * Check out a guest
   */
  async checkOut(
    passId: string,
    notes?: string
  ): Promise<GuestPassResponse> {
    const response = await axiosInstance.post(`${this.baseUrl}/${passId}/check-out`, { notes });
    return response.data;
  }

  /**
   * Cancel a guest pass
   */
  async cancel(passId: string, reason?: string): Promise<GuestPassResponse> {
    const response = await axiosInstance.post(`${this.baseUrl}/${passId}/cancel`, { reason });
    return response.data;
  }

  /**
   * Mark guest as no-show
   */
  async markNoShow(passId: string): Promise<GuestPassResponse> {
    const response = await axiosInstance.post(`${this.baseUrl}/${passId}/no-show`);
    return response.data;
  }

  /**
   * Update guest pass details
   */
  async update(passId: string, data: Partial<GuestPass>): Promise<GuestPassResponse> {
    const response = await axiosInstance.put(`${this.baseUrl}/${passId}`, data);
    return response.data;
  }

  /**
   * Get pass by pass code (for check-in lookup)
   */
  async getByPassCode(passCode: string): Promise<GuestPassResponse> {
    const response = await axiosInstance.get(`${this.baseUrl}/code/${passCode}`);
    return response.data;
  }
}

// Export singleton instance
export const guestPassService = new GuestPassService();
export default guestPassService;
